import { forwardRef, useId } from 'react'
import { useTranslation } from 'react-i18next'

import UIIcon from '@/components/UI/UIIcon'

export type InputRangeCursorProps = React.HTMLProps<HTMLDivElement> & {
  dragging: boolean
  currentIndex: number
  currentLabel: string
}

const InputRangeCursor = forwardRef<HTMLDivElement, InputRangeCursorProps>(
  ({ className, dragging, currentIndex, currentLabel, style }, ref) => {
    const { t } = useTranslation()

    const getScale = (index: number) => {
      if (currentIndex === index) {
        if (dragging) {
          return 'scale-[100%]'
        }
        return 'scale-[80%]'
      } else {
        return 'scale-[80%] rotate-45'
      }
    }

    const id = useId()

    // define text path start offset depending on the current text length
    const pathLength = Math.PI * 80 // radius of the circle
    const textLength = t('questions.likert.instruction').length
    const startOffset = (pathLength / textLength) * Math.PI

    return (
      <div
        ref={ref}
        className={`InputRangeCursor ${className ? className : ''} w-32 h-32 md:w-160 md:h-160 grid items-center justify-center pointer-events-auto cursor-pointer content-center justify-items-center *:pointer-events-none`}
        style={style}
        aria-hidden
      >
        <div className='col-start-1 col-span-1 row-start-1 row-span-1 w-full h-full'>
          <div
            className={`duration-300 ease-out-quad ${dragging ? 'transition-all rotate-45 opacity-0 scale-110' : 'transition-opacity'}`}
          >
            <svg viewBox='0 0 160 160' className='w-full h-full'>
              <defs>
                <path
                  id={`circlePath-${id}`}
                  d='M 80, 80 m -70, 0 a 70,70 0 0,0 140,0 a 70,70 0 1,0 -140,0Z'
                  fill='none'
                  stroke='black'
                  strokeWidth='1'
                />
              </defs>
              <text>
                <textPath
                  href={`#circlePath-${id}`}
                  startOffset={startOffset}
                  className='antialiased font-body font-semibold leading-relaxed tracking-wide text-14'
                >
                  {t('questions.likert.instruction')}
                </textPath>
              </text>
            </svg>
          </div>
        </div>

        <UIIcon
          icon='range1'
          className={`text-[120px] md:text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${dragging ? 'text-coral' : 'text-peach'} ${getScale(0)}`}
        />

        {/*<UIIcon icon="range1" className={`text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${ dragging ? 'text-light-blue' : 'text-peach' } ${ currentIndex === 0 ? 'opacity-100' : 'opacity-0' } ${getScale(0)}`} />
      <UIIcon icon="range2" className={`text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${ dragging ? 'text-mid-blue' : 'text-peach' } ${ currentIndex === 1 ? 'opacity-100' : 'opacity-0' } ${getScale(1)}`} />
      <UIIcon icon="range3" className={`text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${ dragging ? 'text-dark-blue' : 'text-peach' } ${ currentIndex === 2 ? 'opacity-100' : 'opacity-0' } ${getScale(2)}`} />
      <UIIcon icon="range4" className={`text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${ dragging ? 'text-coral' : 'text-peach' } ${ currentIndex === 3 ? 'opacity-100' : 'opacity-0' } ${getScale(3)}`} />
      <UIIcon icon="range5" className={`text-144 col-start-1 col-span-1 row-start-1 row-span-1 transition-all duration-300 ease-out-quad ${ dragging ? 'text-scarlet' : 'text-peach' } ${ currentIndex === 4 ? 'opacity-100' : 'opacity-0' } ${getScale(4)}`} /> */}
        {/* <span className={`absolute top-1/2 mt-58 left-1/2 -translate-x-1/2 whitespace-nowrap px-16 py-14 min-h-40 bg-dark-cream rounded-8 transition-all duration-500 ease-out-quad ${dragging ? 'opacity-0' : 'opacity-100'}`}>{ t('questions.likert.drag') }</span> */}
        <span
          className={`hidden absolute top-0 left-0 w-full h-full md:flex items-center justify-center p-32 text-center text-cream text-body-s transition-opacity duration-500 ease-out-quad ${dragging ? 'opacity-100' : 'opacity-0'}`}
        >
          {currentLabel}
        </span>

        <span className='relative flex items-center col-start-1 col-span-1 row-start-1 row-span-1 self-center justify-self-center md:gap-8'>
          <UIIcon
            icon='previous'
            className={`text-20 md:text-20 text-dark-blue transition-transform duration-300 ease-out-quad ${dragging ? '-translate-x-[250%]' : ''}`}
          />
          <UIIcon
            icon='circle'
            className={`text-8 md:text-12 text-dark-blue transition-opacity duration-500 ease-out-quad ${dragging ? 'opacity-0' : ''}`}
          />
          <UIIcon
            icon='next'
            className={`text-20 md:text-20 text-dark-blue transition-transform duration-300 ease-out-quad ${dragging ? 'translate-x-[250%]' : ''}`}
          />
        </span>
      </div>
    )
  }
)

export default InputRangeCursor
